import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { SelectionState } from "types";

export interface Item {
    id: ID;
    object?: string;
    source?: string;
}

export const defaultSelection: SelectionState = {
    all: false,
    selected: [],
    storedIds: [], // Add optional property for storing IDs
};

export const useSelection = (
    items: Item[],
    isSubset: boolean,
    initialSelection: SelectionState = defaultSelection,
    enableReinitialize = true, // should be false when initial state unchangeable
    onSelectionChange?: (state: SelectionState) => void,
) => {
    const [state, setState] = useState<SelectionState>(initialSelection);
    const isSelectedAll = state.all || (isSubset && state.selected.length === items.length);
    const hasSelection = isSelectedAll || state.selected.length > 0;

    useEffect(() => {
        const isDifferentSelection =
            state.all !== initialSelection.all || !isEqual(state.selected, initialSelection.selected);

        if (isDifferentSelection && enableReinitialize) {
            setState(initialSelection);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelection]);

    /**
     * Check if an item is selected.
     */
    const isSelected = (item: Item) => {
        if (state.all) return true;

        return state.selected.some((selectedId) => {
            if (Array.isArray(selectedId) && Array.isArray(item.id)) {
                return (
                    selectedId.length === item.id.length && selectedId.every((value, index) => value === item.id[index])
                );
            }

            return selectedId === item.id;
        });
    };

    /**
     * Toggle item selection.
     */
    const select = (item: Item) => {
        const storedIds = state.storedIds ?? [];
        const newStoredIds = storedIds.includes(item.id)
            ? storedIds.filter((id) => !arraysEqual(id, item.id))
            : [...storedIds, item.id];

        let newState: SelectionState;

        if (state.all) {
            if (isSubset) {
                newState = { all: false, selected: [item.id], storedIds: newStoredIds };
            } else {
                const selected = items.map((item) => item.id).filter((id) => !arraysEqual(id, item.id)); // filter out item.id if it's an array
                newState = { all: false, selected, storedIds: newStoredIds };
            }

            setState(newState);
            onSelectionChange?.(newState);
            return;
        }

        const selected = isSelected(item)
            ? state.selected.filter((id) => !arraysEqual(id, item.id)) // use arraysEqual for correct comparison
            : [...state.selected, item.id];

        newState =
            !isSubset && selected.length === items.length
                ? { all: true, selected: [], storedIds: newStoredIds }
                : { all: false, selected, storedIds: newStoredIds };

        setState(newState);
        onSelectionChange?.(newState);
    };

    /**
     * Toggle select all.
     * @param force - Optional parameter to force the selection state.
     *                If true, forces all items to be selected.
     *                If false, forces all items to be deselected.
     *                If undefined, follows the existing logic.
     */
    const selectAll = (force?: boolean) => {
        const allIds = items.map((item) => item.id);
        let newState: SelectionState;

        if (force !== undefined) {
            // Forcefully set selection state
            newState = { all: force, selected: [], storedIds: force ? allIds : [] };
            setState(newState);
            onSelectionChange?.(newState);
            return;
        }

        if (isSubset) {
            // This is just the subset of data, select all available items.
            const selected = isSelectedAll ? [] : items.map((item) => item.id);
            newState = {
                all: false,
                selected,
                storedIds: isSelectedAll ? [] : [...(state.storedIds ?? []), ...selected],
            };
            setState(newState);
            onSelectionChange?.(newState);
        } else if (state.all) {
            // All was checked, uncheck it
            newState = { ...state, all: false, storedIds: [] };
            setState(newState);
            onSelectionChange?.(newState);
        } else {
            // All was unchecked, check it
            newState = { all: true, selected: [], storedIds: allIds };
            setState(newState);
            onSelectionChange?.(newState);
        }
    };

    /**
     * Add items to the selection.
     */
    const addToSelection = (itemsToAdd: Item[]) => {
        const itemsToAddIds = itemsToAdd.map((item) => item.id);

        // Merge unique IDs into the current selection
        const newSelected = [
            ...state.selected,
            ...itemsToAddIds.filter((id) => !state.selected.some((selectedId) => arraysEqual(selectedId, id))),
        ];
        const newStoredIds = [
            ...(state.storedIds ?? []),
            ...itemsToAddIds.filter((id) => !(state.storedIds ?? []).some((storedId) => arraysEqual(storedId, id))),
        ];

        if (!isSubset && newSelected.length === items.length) {
            setState({ all: true, selected: [], storedIds: newStoredIds });
        } else {
            setState({ all: false, selected: newSelected, storedIds: newStoredIds });
        }
    };

    /**
     * Remove items from the selection.
     */
    const deselectFromSelection = (itemsToRemove: Item[]) => {
        const itemsToRemoveIds = itemsToRemove.map((item) => item.id);

        // Filter out the IDs that need to be removed
        const newSelected = state.selected.filter(
            (selectedId) => !itemsToRemoveIds.some((id) => arraysEqual(id, selectedId)),
        );
        const newStoredIds = (state.storedIds ?? []).filter(
            (storedId) => !itemsToRemoveIds.some((id) => arraysEqual(id, storedId)),
        );

        // If no items are left after deselection, clear all selected and stored IDs
        if (newSelected.length === 0 && newStoredIds.length === 0) {
            setState({ all: false, selected: [], storedIds: [] });
        } else {
            setState({ all: false, selected: newSelected, storedIds: newStoredIds });
        }
    };

    return {
        state,
        setState,
        select,
        selectAll,
        isSelected,
        addToSelection,
        deselectFromSelection,
        isSelectedAll,
        hasSelection,
        count: state.all ? "All" : state.selected.length,
    };
};

// Helper function to check if two IDs (or arrays) are equal
const arraysEqual = (array1: any, array2: any): boolean => {
    if (Array.isArray(array1) && Array.isArray(array2)) {
        return array1.length === array2.length && array1.every((value, index) => value === array2[index]);
    }

    return array1 === array2;
};

export { type SelectionState } from "types";
